import { customerAccessTokenAtom } from "../atoms/authAtoms";
import { API, GraphQLResult } from "@aws-amplify/api";
import { getOrders } from "../graphql/queries";
import { Order, OrderResult } from "gpc-shared-types";
import { useAtom } from "jotai";
import { useState } from "react";

interface OrdersPageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string;
  endCursor: string;
}

type OrdersResult = GraphQLResult<{
  orders: {
    pageInfo: OrdersPageInfo;
    edges: {
      cursor: string;
      node: OrderResult;
    }[];
  };
}>;

interface UseOrdersProps {
  pageSize?: number;
}

interface BaseFilterProps {
  dateFilter: string | number;
  searchKeyword: string;
}

interface FetchOrdersProps extends BaseFilterProps {
  before?: string;
  after?: string;
  customerId?: string;
}

const buildQuery = ({ dateFilter, searchKeyword }: BaseFilterProps) => {
  let query;
  const today = new Date();
  const pastDate = new Date(today);
  if (dateFilter === "Last 60 days") {
    pastDate.setDate(today.getDate() - 60);
    const dateSixtyDaysBefore = pastDate.toLocaleDateString("en-AU", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    query = `created_at:>${dateSixtyDaysBefore}`;
  } else {
    query = `created_at:>01/01/${Number(dateFilter)} AND created_at:<01/01/${Number(dateFilter) + 1}`;
  }

  if (searchKeyword) {
    query = query + ` AND '${searchKeyword}'`;
  }
  return query;
};

export const useOrders = ({ pageSize = 5 }: UseOrdersProps) => {
  const [customerAccessToken] = useAtom(customerAccessTokenAtom);
  const [orders, setOrders] = useState<(Order & { cursor: string })[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [ordersPageInfo, setOrdersPageInfo] = useState<OrdersPageInfo>();

  const fetchOrders = async ({
    before,
    after,
    dateFilter,
    searchKeyword,
    customerId,
  }: FetchOrdersProps) => {
    if (!customerId && !customerAccessToken) {
      console.error(
        "Missing required parameters. Must provide either customerId or customerAccessToken",
      );
      return;
    }

    const query = buildQuery({ dateFilter, searchKeyword });

    setLoading(true);
    const isFetchingFirstPage = !after && !before;
    const first = after || isFetchingFirstPage ? pageSize : undefined;
    const last = before ? pageSize : undefined;

    try {
      const { data } = (await API.graphql({
        query: getOrders,
        variables: {
          customerAccessToken,
          customerId,
          first,
          after,
          last,
          before,
          query,
        },
      })) as OrdersResult;

      const orders =
        data?.orders.edges.map((edge) => ({
          ...edge.node,
          lineItems: (edge.node.lineItems?.edges || []).map(
            (edge) => edge.node,
          ),
          hasMoreLineItems: edge.node.lineItems.pageInfo.hasNextPage,
          cursor: edge.cursor,
        })) || [];

      setOrders(orders);
      setOrdersPageInfo(data?.orders?.pageInfo);
    } catch (err: any) {
      if (err.errors) {
        console.error(
          "Unable to fetch orders",
          err.errors.flatMap((e: any) => JSON.parse(e.message)),
        );
        setError(err);
      } else {
        console.error("An unknown error occurred fetching orders", err);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    orders,
    loading,
    error,
    ordersPageInfo,
    fetchOrders,
  };
};
